"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.inventoryImagePrefix = exports.inventoryFirestorePath = exports.storagePaths = exports.firestorePaths = exports.operationClaimDocumentId = exports.requireSha256 = exports.requireOperationId = exports.requireWorkflow = void 0;
const FIRESTORE_SEGMENT_LIMIT = 1500;
const STORAGE_SEGMENT_LIMIT = 1024;
const WORKFLOW_PATTERN = /^[a-z][a-z0-9-]{0,79}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const RESERVED_FIRESTORE_ID_PATTERN = /^__.*__$/;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;
const utf8Length = (value) => new TextEncoder().encode(value).byteLength;
const segment = (value, label, limit = FIRESTORE_SEGMENT_LIMIT) => {
    if (!value ||
        value === '.' ||
        value === '..' ||
        value.includes('/') ||
        CONTROL_CHARACTER_PATTERN.test(value) ||
        RESERVED_FIRESTORE_ID_PATTERN.test(value) ||
        utf8Length(value) > limit) {
        throw new Error(`Invalid ${label}.`);
    }
    return value;
};
const requireWorkflow = (workflow) => {
    if (!WORKFLOW_PATTERN.test(workflow))
        throw new Error('Invalid workflow.');
    return workflow;
};
exports.requireWorkflow = requireWorkflow;
const requireOperationId = (operationId) => {
    if (!UUID_PATTERN.test(operationId))
        throw new Error('Invalid operation ID.');
    return operationId.toLowerCase();
};
exports.requireOperationId = requireOperationId;
const requireSha256 = (value, label = 'SHA-256 value') => {
    if (!SHA256_PATTERN.test(value))
        throw new Error(`Invalid ${label}.`);
    return value;
};
exports.requireSha256 = requireSha256;
const operationClaimDocumentId = (workflow, operationId) => `${(0, exports.requireWorkflow)(workflow)}~${(0, exports.requireOperationId)(operationId)}`;
exports.operationClaimDocumentId = operationClaimDocumentId;
exports.firestorePaths = {
    freeAiDailyBudget: () => 'internalDispatchState/freeAiDailyBudget',
    collection: (collectionId) => `collections/${segment(collectionId, 'collection ID')}`,
    member: (collectionId, userId) => `${exports.firestorePaths.collection(collectionId)}/members/${segment(userId, 'user ID')}`,
    item: (collectionId, itemId) => `${exports.firestorePaths.collection(collectionId)}/items/${segment(itemId, 'item ID')}`,
    location: (collectionId, locationId) => `${exports.firestorePaths.collection(collectionId)}/locations/${segment(locationId, 'location ID')}`,
    itemDeletionJob: (collectionId, operationId) => `${exports.firestorePaths.collection(collectionId)}/itemDeletionJobs/${(0, exports.requireOperationId)(operationId)}`,
    invitationMail: (operationId) => `invitationMail/${(0, exports.requireOperationId)(operationId)}`,
    invitationRateLimit: (key) => `invitationRateLimits/${segment(key, 'rate limit key')}`,
    invitation: (collectionId, invitationId) => `${exports.firestorePaths.collection(collectionId)}/invitations/${segment(invitationId, 'invitation ID')}`,
    transfer: (collectionId, transferId) => `${exports.firestorePaths.collection(collectionId)}/transfers/${segment(transferId, 'transfer ID')}`,
    auditEvent: (collectionId, eventId) => `${exports.firestorePaths.collection(collectionId)}/auditEvents/${segment(eventId, 'audit event ID')}`,
    capacityState: (collectionId) => `${exports.firestorePaths.collection(collectionId)}/internalState/capacity`,
    aiPolicy: (collectionId) => `${exports.firestorePaths.collection(collectionId)}/internalState/aiPolicy`,
    aiCacheEntry: (collectionId, cacheKey) => `${exports.firestorePaths.collection(collectionId)}/aiCache/${(0, exports.requireSha256)(cacheKey, 'AI cache key')}`,
    imageDeletionFence: (collectionId) => `${exports.firestorePaths.collection(collectionId)}/internalState/imageDeletionFence`,
    imageDeletionFenceScanState: () => 'internalDispatchState/imageDeletionFenceRecovery',
    capacityReservation: (collectionId, reservationId) => `${exports.firestorePaths.collection(collectionId)}/capacityReservations/${segment(reservationId, 'reservation ID')}`,
    capacityReconciliation: (collectionId, reconciliationId) => `${exports.firestorePaths.collection(collectionId)}/capacityReconciliations/${segment(reconciliationId, 'reconciliation ID')}`,
    collectionOperationClaim: (collectionId, workflow, operationId) => `${exports.firestorePaths.collection(collectionId)}/operationClaims/${(0, exports.operationClaimDocumentId)(workflow, operationId)}`,
    operationBinding: (operationId) => `operationBindings/${(0, exports.requireOperationId)(operationId)}`,
    billingAccount: (billingAccountId) => `billingAccounts/${segment(billingAccountId, 'billing account ID')}`,
    billingAccountOwner: (ownerUserId) => `billingAccountOwners/${segment(ownerUserId, 'owner user ID')}`,
    billingProvisioningConflict: (ownerUserId) => `billingProvisioningConflicts/${segment(ownerUserId, 'owner user ID')}`,
    billingProvisioningAuditEvent: (billingAccountId, eventId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/auditEvents/${segment(eventId, 'event ID')}`,
    aiAllowance: (billingAccountId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/internalState/aiAllowance`,
    creditBalance: (billingAccountId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/internalState/creditBalance`,
    itemBilling: (billingAccountId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/internalState/itemBilling`,
    creditTransaction: (billingAccountId, eventId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/creditTransactions/${segment(eventId, 'ledger event ID')}`,
    usageOperation: (billingAccountId, operationId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/usageOperations/${(0, exports.requireOperationId)(operationId)}`,
    purchase: (billingAccountId, purchaseId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/purchases/${segment(purchaseId, 'purchase ID')}`,
    billingOperationClaim: (billingAccountId, workflow, operationId) => `${exports.firestorePaths.billingAccount(billingAccountId)}/operationClaims/${(0, exports.operationClaimDocumentId)(workflow, operationId)}`,
    internalJob: (jobId) => `internalJobs/${segment(jobId, 'job ID')}`,
    capture: (collectionId, operationId) => `${exports.firestorePaths.collection(collectionId)}/captures/${(0, exports.requireOperationId)(operationId)}`,
    captureAdmission: (operationId) => `captureAdmissions/${(0, exports.requireOperationId)(operationId)}`,
    captureUploadState: (collectionId) => `${exports.firestorePaths.collection(collectionId)}/internalState/captureUploads`,
    captureStatus: (collectionId, operationId) => `${exports.firestorePaths.collection(collectionId)}/captureStatuses/${(0, exports.requireOperationId)(operationId)}`,
    imageOperation: (collectionId, operationId) => `${exports.firestorePaths.collection(collectionId)}/imageOperations/${(0, exports.requireOperationId)(operationId)}`,
    stripeCustomerMapping: (stripeCustomerId) => `stripeCustomerMappings/${segment(stripeCustomerId, 'Stripe customer ID')}`,
    stripeEventClaim: (stripeEventId) => `stripeEventClaims/${segment(stripeEventId, 'Stripe event ID')}`,
    usernameClaim: (normalizedUsername) => `usernames/${segment(normalizedUsername, 'normalized username')}`,
};
const storageSegment = (value, label) => segment(value, label, STORAGE_SEGMENT_LIMIT);
const storageObjectPath = (value) => {
    if (utf8Length(value) > STORAGE_SEGMENT_LIMIT)
        throw new Error('Storage object path is too long.');
    return value;
};
exports.storagePaths = {
    captureUpload: (collectionId, operationId) => storageObjectPath(`collections/${storageSegment(collectionId, 'collection ID')}/uploads/${(0, exports.requireOperationId)(operationId)}.jpg`),
    durableImage: (collectionId, opaqueFileName) => storageObjectPath(`collections/${storageSegment(collectionId, 'collection ID')}/images/${storageSegment(opaqueFileName, 'file name')}`),
};
const inventoryFirestorePath = (authority, kind) => {
    segment(authority.collectionId, 'collection ID');
    segment(authority.userId, 'user ID');
    if (authority.compatibilityMode === 'collection-only') {
        return `${exports.firestorePaths.collection(authority.collectionId)}/${kind}`;
    }
    throw new Error('Inventory compatibility mode is unsupported.');
};
exports.inventoryFirestorePath = inventoryFirestorePath;
const inventoryImagePrefix = (authority) => {
    storageSegment(authority.collectionId, 'collection ID');
    storageSegment(authority.userId, 'user ID');
    if (authority.compatibilityMode === 'collection-only') {
        return `collections/${authority.collectionId}/images`;
    }
    throw new Error('Inventory compatibility mode is unsupported.');
};
exports.inventoryImagePrefix = inventoryImagePrefix;
